//@ts-check

if (!customElements.get("ff-select")) {
  class FfSelect extends HTMLElement {
    constructor() {
      super();
      this.style.pointerEvents = "none";

      /**
       * @type {HTMLInputElement}
       */
      this.ffInput = document.querySelector(this.dataset.ffInputSelector);

      /**
       * @type {HTMLDivElement}
       */
      this.ffOptionsContainerEl = this.querySelector(
        "[data-ff-select-options]"
      );

      /**
       * @type {NodeListOf<HTMLButtonElement>}
       */
      this.ffOpenCloseEl = this.querySelectorAll("[data-ff-select-open-close]");

      /**
       * @type {HTMLSpanElement}
       */
      this.ffResultEl = this.querySelector("[data-ff-select-selection]");

      /**
       * @type {HTMLSpanElement}
       */
      this.ffPlaceholderEl = this.querySelector("[data-ff-select-placeholder]");

      this.ffOpenCloseEl.forEach((openCloseEl) =>
        openCloseEl.addEventListener("click", (e) => {
          if (!e.target.classList.contains("ff-select-not-open-close--js")) {
            this.ffOpenClose();
          }
        })
      );

      this.ffInit();
    }

    ffHandleDocumentClick = (event) => {
      if (
        !this.contains(event.target) &&
        !document.getElementById("ff-select-portal").contains(event.target)
      ) {
        this.ffClose();
      }
    };

    /**
     * Calculate available space and set dropdown alignment (horizontal and vertical)
     */
    ffCalculateDropdownDirection() {
      const rect = this.getBoundingClientRect();
      const spaceOnLeft = rect.left;
      const spaceOnRight = window.innerWidth - rect.right;

      // Horizontal alignment
      if (spaceOnRight >= spaceOnLeft) {
        this.ffOptionsContainerEl.style.left = "0";
        this.ffOptionsContainerEl.style.right = "auto";
      } else {
        this.ffOptionsContainerEl.style.left = "auto";
        this.ffOptionsContainerEl.style.right = "0";
      }

      // Set max-width based on available space
      const maxSpace = Math.max(spaceOnLeft, spaceOnRight);
      const maxWidth = Math.min(maxSpace - 20, 400); // 20px margin, 400px max
      this.ffOptionsContainerEl.style.maxWidth = `${maxWidth}px`;

      // Vertical alignment - only for smart mode (upward/downward handled by CSS)
      const verticalAlign = this.dataset.ffOptionsVerticalAlign || "smart";
      if (verticalAlign === "smart") {
        const spaceAbove = rect.top;
        const spaceBelow = window.innerHeight - rect.bottom;

        // Add position class based on available space
        if (spaceBelow >= spaceAbove) {
          this.classList.add("ff-select--position-downward");
          this.classList.remove("ff-select--position-upward");
        } else {
          this.classList.add("ff-select--position-upward");
          this.classList.remove("ff-select--position-downward");
        }
      }
    }

    ffOpenClose() {
      if (this.ffIsOpen) {
        this.ffClose();
      } else {
        this.ffOpen();
      }
    }

    ffClose() {
      this.classList.remove("ff-select-open");
      this.ffIsOpen = false;
      document.removeEventListener("click", this.ffHandleDocumentClick, true);
      document.getElementById("ff-select-portal").ffHide();
    }

    ffOpen() {
      this.ffCalculateDropdownDirection();
      this.classList.add("ff-select-open");
      this.ffIsOpen = true;
      document.addEventListener("click", this.ffHandleDocumentClick, true);
      document.getElementById("ff-select-portal").ffShow(this);
    }

    /**
     * Animates text replacement with a CSS clip-path reveal effect
     * @param {HTMLElement} element - The element to animate
     * @param {string} newText - The new text to display
     */
    animateTextReplacement(element, newText) {
      if (!element || !newText) return;

      // Add animation class and set new text
      if (element.classList.contains("ff-text-reveal")) {
        element.classList.replace("ff-text-reveal", "ff-text-reveal-again");
      } else if (element.classList.contains("ff-text-reveal-again")) {
        element.classList.replace("ff-text-reveal-again", "ff-text-reveal");
      } else {
        element.classList.add("ff-text-reveal");
      }

      element.textContent = newText;
    }

    ffInit() {
      if (this.blockedEl) {
        this.blockedEl.classList.remove("ff-button-container--blocked");
        this.blockedElChildren?.forEach((child) => {
          if (child instanceof HTMLElement) {
            child.style.display = "";
          }
        });
        this.blockedElMsgEl?.remove();
      }

      this.ffOptionsEl = this.querySelectorAll(".ff-select-option");

      this.setAttribute("data-ff-select-initialized", "true");
      this.style.pointerEvents = "";
      if (this.dataset.hasOwnProperty("ffSelectHideUntilInit")) {
        this.style.display = "";
      }
      this.classList.remove("ff-select-option-selected");

      this.ffPlaceholderEl.style.display = "";
      this.ffResultEl.textContent = "";
      if (this.ffInput) this.ffInput.value = "";

      const {
        ffOverrideTextSelector: ffSelectOverrideTextSelector,
        ffBlockUntilSelectionSelector: ffSelectBlockUntilSelectionSelector,
        ffBlockUntilSelectionMsg: ffSelectBlockUntilSelectionMsg,
        ffSelectSingleSelection: ffSelectSingleSelection,
      } = this.dataset;

      if (ffSelectBlockUntilSelectionSelector) {
        /**
         * @type {HTMLButtonElement | null}
         */
        this.blockedEl = document.querySelector(
          ffSelectBlockUntilSelectionSelector
        );

        if (this.blockedEl) {
          this.blockedEl.classList.add("ff-button-container--blocked");
          if (ffSelectBlockUntilSelectionMsg) {
            this.blockedElChildren = Array.from(this.blockedEl.children);
            this.blockedElChildren.forEach((child) => {
              if (child instanceof HTMLElement) {
                child.style.display = "none";
              }
            });
            this.blockedElMsgEl = document.createElement("span");
            this.blockedElMsgEl.textContent = ffSelectBlockUntilSelectionMsg;
            this.blockedEl.appendChild(this.blockedElMsgEl);
          }
        }
      }

      this.ffOptionsEl.forEach((optionEl) => {
        const {
          ffValue,
          ffOverrideText,
          ffNewPlaceholder,
          ffPlaceholderVariation,
        } = optionEl.dataset;
        optionEl.addEventListener("click", (e) => {
          e.stopPropagation();
          if (ffSelectSingleSelection == "true") {
            this.ffClose();

            if (this.ffInput) this.ffInput.value = ffValue;

            this.ffOptionsEl.forEach((option) => {
              option.classList.remove("ff-select_selected");
            });
            this.ffPlaceholderEl.style.display = "none";

            if (
              this.querySelector(".ff-select-placeholder").innerHTML.includes(
                ffNewPlaceholder
              )
            ) {
              this.animateTextReplacement(
                this.ffResultEl,
                ffPlaceholderVariation
              );
            } else {
              this.animateTextReplacement(this.ffResultEl, ffNewPlaceholder);
            }

            optionEl.classList.add("ff-select_selected");
            this.classList.add("ff-select-option-selected");
          }

          if (this.blockedEl) {
            //This component blocks the button without the attribute disabled, so we need to check if it's not disabled
            if (!this.blockedEl.disabled) {
              this.blockedEl.classList.replace(
                "ff-button-container--blocked",
                "ff-button-container--success"
              );
            }
            this.blockedElChildren?.forEach((child) => {
              if (child instanceof HTMLElement) {
                child.style.display = "";
              }
            });
            this.blockedElMsgEl?.remove();
          }

          if (ffSelectOverrideTextSelector) {
            const textEl = document.querySelector(ffSelectOverrideTextSelector);
            if (textEl) {
              this.animateTextReplacement(textEl, ffOverrideText);
            }
          }
        });
      });
    }
  }

  customElements.define("ff-select", FfSelect);
}
